import { useCustomerAutoshipSubscriptions, useDecisions, useInvalidateAutoshipSubscriptions } from '@libs/common-next';
import { type DrawerContent, useCartConfirmDrawer } from '@libs/shared-ui';

import type { AddSubscriptionResponse, PostOrdergrooveAddSubscription } from '@/entities/autoship-subscription';
import { ImageType, useProduct } from '@/entities/product';
import { useFulfilmentDrawerStore } from '@/features/fulfilment-selection';

import { useAutoshipAnalyticsEvent, useViewAutoshipOrder } from '../analytics';

type OpenDrawerParams = {
    productId: string;
    frequencyAmount: Nullable<number>;
    unitOfMeasure: Nullable<number>;
};

export const useOnSuccessExistingAutoship = () => {
    const autoshipFlag = useDecisions('ff_autoship');

    const { base } = useProduct();
    const { autoshipData } = useCustomerAutoshipSubscriptions();
    const nextSubscriptionDate = autoshipData?.data?.nextSubscriptionDate;
    const invalidateAutoshipSubscriptions = useInvalidateAutoshipSubscriptions();

    const closeFulfilmentDrawer = useFulfilmentDrawerStore(state => state.close);
    const openCartDrawerWith = useCartConfirmDrawer(state => state.openWith);

    const { send: sendViewAutoshipOrder } = useViewAutoshipOrder();
    const { send: sendAutoshipAnalytics } = useAutoshipAnalyticsEvent();

    const openConfirmDrawer = ({ productId, frequencyAmount, unitOfMeasure }: OpenDrawerParams) => {
        const addedProduct = base?.variants[productId];
        const firstImage = addedProduct?.media?.find(item => item.type === 'image') as ImageType | undefined;

        const itemForDrawer = {
            name: addedProduct?.name ?? '',
            image: firstImage?.url ?? '',
            sku: productId,
            autoshipFrequency: frequencyAmount,
            autoshipFrequencyUnitOfMeasure: unitOfMeasure,
        };

        const nextAutoshipDate = nextSubscriptionDate
            ? new Date(nextSubscriptionDate).toLocaleString('en-US', {
                  month: 'short',
                  weekday: 'short',
                  day: 'numeric',
                  timeZone: 'UTC',
              })
            : null;

        const drawerContent: DrawerContent = {
            title: 'Added to Autoship',
            subtitle: nextAutoshipDate ? `Item(s) added to your next Autoship order on ${nextAutoshipDate}.` : '',
            primaryAction: { text: 'View order', href: '/account/auto-ship', onClick: sendViewAutoshipOrder },
        };

        closeFulfilmentDrawer();
        openCartDrawerWith([itemForDrawer], drawerContent);
    };

    const onSuccess = (response: AddSubscriptionResponse, variables: PostOrdergrooveAddSubscription) => {
        const { public_id } = response;
        const { quantity, frequencyAmount, unitOfMeasure, isOneTimeOrder, productId } = variables;

        autoshipFlag === 'crawl' && openConfirmDrawer({ productId, frequencyAmount, unitOfMeasure });

        sendAutoshipAnalytics({
            quantity,
            public_id,
            isOneTimeOrder,
            unitOfMeasure,
            frequencyAmount,
        });

        invalidateAutoshipSubscriptions();
    };

    return { onSuccess };
};
